import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, Film, Loader2, Monitor, Trash2, Upload,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useToast } from '../components/ui/use-toast';
import { confirm } from '../components/ui/confirm-dialog';
import AccessDenied from '../components/AccessDenied';
import { useRestaurant } from '../context/RestaurantContext';
import { useModuleAccess } from '../utils/useModuleAccess';
import { useT } from '../context/LanguageContext';
import { deviceService } from '../services/deviceService';
import { locationsService } from '../services/locationsService';
import { kioskAdsService, kioskSettingsService } from '../services/kioskAdsService';

/** Kiosk Settings (Settings → Kiosk Settings): which kiosks a restaurant has, branch by
    branch, what each branch plays while nobody is at them, and how long a kiosk waits
    before clearing an order somebody walked away from. */

// Five seconds is the advice the screen gives for a clip, not a rule the
// server enforces — a counter knows its own queue better than we do.
const CLIP_ADVISED_SECONDS = 5;

/* A kiosk is only ever in one of four states, and three of them mean "not
   serving". Colour says which at a glance; the word says which one. */
const STATUS_TONE = {
  ACTIVE: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  PENDING: 'bg-amber-100 text-amber-800 border-amber-200',
  REVOKED: 'bg-muted text-muted-foreground border-border',
  DENIED: 'bg-red-100 text-red-800 border-red-200',
};

function StatusPill({ status, t }) {
  const key = `kiosk.settings.status.${String(status || '').toLowerCase()}`;
  const label = t(key);
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium
                      ${STATUS_TONE[status] || STATUS_TONE.REVOKED}`}>
      {label === key ? status : label}
    </span>
  );
}

/* ------------------------------------------------------------------ ads --
   One branch's rotation. Posters and clips live in the same list because they
   play in the same loop — separating them on screen would suggest two
   rotations, and there is one. */
function AdsPanel({ restaurantId, location, canEdit, t }) {
  const { toast } = useToast();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { ads: rows } = await kioskAdsService.list(restaurantId, location.id);
      setAds(rows);
    } catch (e) {
      toast({ title: t('kiosk.settings.ads.loadFailed'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [restaurantId, location.id, toast, t]);

  useEffect(() => { load(); }, [load]);

  const choose = async (e) => {
    const file = e.target.files?.[0];
    // Let the same file be chosen again after a failed upload.
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      await kioskAdsService.upload(restaurantId, location.id, file);
      await load();
      toast({ title: t('kiosk.settings.ads.added') });
    } catch (err) {
      toast({
        title: err?.response?.data?.error || t('kiosk.settings.ads.uploadFailed'),
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (ad) => {
    setBusy(true);
    try {
      await kioskAdsService.setActive(restaurantId, location.id, ad.id, !ad.is_active);
      await load();
    } catch (e) {
      toast({ title: t('kiosk.settings.ads.saveFailed'), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (ad) => {
    const ok = await confirm({
      title: t('kiosk.settings.ads.removeTitle'),
      description: t('kiosk.settings.ads.removeBody', { name: ad.name || '' }),
      confirmText: t('kiosk.settings.ads.remove'),
    });
    if (!ok) return;
    setBusy(true);
    try {
      await kioskAdsService.remove(restaurantId, location.id, ad.id);
      await load();
    } catch (e) {
      toast({ title: t('kiosk.settings.ads.saveFailed'), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 border-t border-border pt-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-foreground">{t('kiosk.settings.ads.title')}</div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t('kiosk.settings.ads.hint', { seconds: CLIP_ADVISED_SECONDS })}
          </p>
        </div>
        {canEdit && (
          <>
            <input ref={fileRef} type="file" className="hidden"
              accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime"
              onChange={choose} />
            <Button type="button" size="sm" variant="outline" disabled={busy}
              onClick={() => fileRef.current?.click()}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              <span className="ml-2">{t('kiosk.settings.ads.add')}</span>
            </Button>
          </>
        )}
      </div>

      {loading ? (
        <div className="py-6 text-center text-sm text-muted-foreground">
          {t('kiosk.settings.loading')}
        </div>
      ) : ads.length === 0 ? (
        /* Not an error: a branch with no ads shows the kiosk's own built-in
           slides, which is a perfectly good counter. */
        <p className="py-4 text-sm text-muted-foreground">{t('kiosk.settings.ads.empty')}</p>
      ) : (
        <ul className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {ads.map((ad) => (
            <li key={ad.id}
              className={`flex gap-3 rounded-lg border border-border p-3 ${ad.is_active ? '' : 'opacity-60'}`}>
              <div className="flex h-16 w-24 shrink-0 items-center justify-center overflow-hidden rounded bg-muted">
                {ad.kind === 'CLIP'
                  ? <Film className="h-6 w-6 text-muted-foreground" />
                  : <img src={ad.url} alt="" className="h-full w-full object-cover" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-foreground" title={ad.name}>
                  {ad.name || (ad.kind === 'CLIP' ? t('kiosk.settings.ads.clip') : t('kiosk.settings.ads.poster'))}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {ad.kind === 'CLIP' ? t('kiosk.settings.ads.clip') : t('kiosk.settings.ads.poster')}
                  {' · '}
                  {ad.is_active ? t('kiosk.settings.ads.playing') : t('kiosk.settings.ads.paused')}
                </div>
                {canEdit && (
                  <div className="mt-2 flex items-center gap-3">
                    <button type="button" className="text-xs text-primary hover:underline"
                      disabled={busy} onClick={() => toggle(ad)}>
                      {ad.is_active ? t('kiosk.settings.ads.pause') : t('kiosk.settings.ads.play')}
                    </button>
                    <button type="button" className="inline-flex items-center gap-1 text-xs text-red-700 hover:underline"
                      disabled={busy} onClick={() => remove(ad)}>
                      <Trash2 className="h-3 w-3" /> {t('kiosk.settings.ads.remove')}
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- idle --
   The branch's kiosk clock. Two numbers rather than one, because they answer
   different questions: how long a guest may read the menu before being asked
   anything, and how long the "Still there?" question waits for an answer.
   Held per branch beside the ads, and read by the kiosk off the same menu
   payload, so a saved change reaches the panel with its next order. */
function IdlePanel({ restaurantId, location, canEdit, t }) {
  const { toast } = useToast();
  const [form, setForm] = useState(null);      // { idle_seconds, countdown_seconds } as typed
  const [saved, setSaved] = useState(null);    // what the server holds, so Save knows it has work
  const [limits, setLimits] = useState({ idle_seconds: [15, 600], countdown_seconds: [5, 120] });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    kioskSettingsService.get(restaurantId, location.id)
      .then((data) => {
        if (!alive) return;
        const next = { idle_seconds: data.idle_seconds, countdown_seconds: data.countdown_seconds };
        setForm(next);
        setSaved(next);
        if (data.limits) setLimits(data.limits);
      })
      .catch(() => {
        if (alive) toast({ title: t('kiosk.settings.idle.loadFailed'), variant: 'destructive' });
      });
    return () => { alive = false; };
  }, [restaurantId, location.id, toast, t]);

  if (!form) return null;

  const [idleMin, idleMax] = limits.idle_seconds;
  const [countMin, countMax] = limits.countdown_seconds;
  const idle = Number(form.idle_seconds);
  const countdown = Number(form.countdown_seconds);
  const valid = Number.isInteger(idle) && idle >= idleMin && idle <= idleMax
    && Number.isInteger(countdown) && countdown >= countMin && countdown <= countMax;
  const dirty = !saved || idle !== saved.idle_seconds || countdown !== saved.countdown_seconds;

  const save = async () => {
    if (!valid) {
      toast({
        title: t('kiosk.settings.idle.invalid', {
          minIdle: idleMin, maxIdle: idleMax, minCount: countMin, maxCount: countMax,
        }),
        variant: 'destructive',
      });
      return;
    }
    setBusy(true);
    try {
      const data = await kioskSettingsService.save(restaurantId, location.id,
        { idle_seconds: idle, countdown_seconds: countdown });
      const next = { idle_seconds: data.idle_seconds, countdown_seconds: data.countdown_seconds };
      setForm(next);
      setSaved(next);
      toast({ title: t('kiosk.settings.idle.saved') });
    } catch (err) {
      toast({
        title: err?.response?.data?.error || t('kiosk.settings.idle.saveFailed'),
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, min, max) => (
    <label className="flex flex-wrap items-center gap-2 text-sm text-foreground">
      <span className="min-w-[13rem]">{label}</span>
      <input type="number" min={min} max={max} step={1} value={form[key]}
        disabled={!canEdit || busy}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        className="h-9 w-24 rounded-md border border-input bg-background px-3 text-sm text-foreground disabled:opacity-60" />
      <span className="text-xs text-muted-foreground">
        {t('kiosk.settings.idle.seconds')} · {min}–{max}
      </span>
    </label>
  );

  return (
    <div className="mt-4 border-t border-border pt-4">
      <div className="text-sm font-medium text-foreground">{t('kiosk.settings.idle.title')}</div>
      <p className="mt-0.5 max-w-3xl text-xs text-muted-foreground">{t('kiosk.settings.idle.hint')}</p>
      <div className="mt-3 flex flex-wrap items-center gap-x-8 gap-y-3">
        {field('idle_seconds', t('kiosk.settings.idle.wait'), idleMin, idleMax)}
        {field('countdown_seconds', t('kiosk.settings.idle.countdown'), countMin, countMax)}
        {canEdit && (
          <Button type="button" size="sm" disabled={busy || !dirty} onClick={save}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t('kiosk.settings.idle.save')}
          </Button>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ page -- */
export default function KioskSettings() {
  const { t } = useT();
  const { restaurantId } = useRestaurant();
  const { canView, canEdit } = useModuleAccess('settings');
  const { toast } = useToast();
  const [devices, setDevices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!restaurantId) return undefined;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      deviceService.list(restaurantId),
      // Inactive branches included: a kiosk still registered to one is exactly
      // what somebody looking at this screen needs to find.
      locationsService.list(restaurantId, { includeInactive: true }),
    ])
      .then(([deviceData, locs]) => {
        if (cancelled) return;
        setDevices(deviceData?.results || []);
        setLocations(locs || []);
      })
      .catch(() => {
        if (cancelled) return;
        toast({ title: t('kiosk.settings.loadFailed'), variant: 'destructive' });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [restaurantId, toast, t]);

  /* Group → Branch → kiosks. The grouping is built from the BRANCH list rather
     than from the devices, so a branch with no kiosk yet still appears: "we
     have no kiosk at Ébène" is the answer somebody opens this screen for. */
  const tree = useMemo(() => {
    const kiosks = devices.filter((d) => d.device_type === 'KIOSK');
    const byLocation = kiosks.reduce((acc, d) => {
      const key = d.location_id || 'none';
      (acc[key] = acc[key] || []).push(d);
      return acc;
    }, {});
    const groups = new Map();
    locations.forEach((loc) => {
      const gid = loc.group_id || 'none';
      const gname = loc.group?.name || t('kiosk.settings.noGroup');
      if (!groups.has(gid)) groups.set(gid, { id: gid, name: gname, branches: [] });
      groups.get(gid).branches.push({ location: loc, kiosks: byLocation[loc.id] || [] });
    });
    // Kiosks whose branch is missing from the list (or that have none) must
    // not vanish — an unassigned terminal is a thing to go and fix.
    const orphans = kiosks.filter((d) => !d.location_id
      || !locations.some((l) => l.id === d.location_id));
    if (orphans.length) {
      groups.set('orphans', {
        id: 'orphans',
        name: t('kiosk.settings.noBranchGroup'),
        branches: [{ location: null, kiosks: orphans }],
      });
    }
    return Array.from(groups.values());
  }, [devices, locations, t]);

  const activeCount = devices.filter(
    (d) => d.device_type === 'KIOSK' && d.status === 'ACTIVE').length;
  const kioskCount = devices.filter((d) => d.device_type === 'KIOSK').length;
  /* The machines that ARE registered but are not kiosks. Without this the
     screen says "no kiosk yet" to somebody looking straight at a running
     terminal, and gives them nowhere to go: the usual answer is that the
     machine paired as a till or a kitchen display, which is a different
     thing and lives on a different screen. */
  const otherKinds = useMemo(() => {
    const counts = devices.reduce((acc, d) => {
      if (d.device_type === 'KIOSK') return acc;
      acc[d.device_type] = (acc[d.device_type] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts).map(([kind, n]) => {
      const key = `kiosk.settings.kind.${kind.toLowerCase()}`;
      const label = t(key);
      return `${n} × ${label === key ? kind : label}`;
    });
  }, [devices, t]);

  if (!canView) return <AccessDenied perm="settings.view" what={t('kiosk.settings.title')} />;

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="font-display text-3xl font-semibold leading-none tracking-tight text-foreground">
          {t('kiosk.settings.title')}
        </h1>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">{t('kiosk.settings.intro')}</p>
      </div>

      {loading ? (
        <div className="rounded-md border border-border bg-card px-4 py-10 text-center text-sm text-muted-foreground">
          {t('kiosk.settings.loading')}
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            {t('kiosk.settings.count', { active: activeCount, total: kioskCount })}
          </p>

          {kioskCount === 0 && (
            /* A restaurant with no kiosk is not broken — it just has not paired
               one yet, and the screen says where that is done. */
            <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {otherKinds.length > 0
                  ? t('kiosk.settings.noneButOthers', { kinds: otherKinds.join(', ') })
                  : t('kiosk.settings.noneYet')}
                {' '}
                {t('kiosk.settings.pairingNote')}
              </span>
            </div>
          )}

          {tree.map((group) => (
            <section key={group.id} className="space-y-3">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {group.name}
              </h2>
              {group.branches.map((branch) => (
                <Card key={branch.location?.id || 'none'}>
                  <CardContent className="p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="font-medium text-foreground">
                        {branch.location?.name || t('kiosk.settings.noBranch')}
                        {branch.location && branch.location.is_active === false && (
                          <span className="ml-2 text-xs font-normal text-muted-foreground">
                            {t('kiosk.settings.branchInactive')}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {t('kiosk.settings.branchCount', {
                          active: branch.kiosks.filter((k) => k.status === 'ACTIVE').length,
                          total: branch.kiosks.length,
                        })}
                      </span>
                    </div>

                    {branch.kiosks.length === 0 ? (
                      <p className="mt-3 text-sm text-muted-foreground">
                        {t('kiosk.settings.branchEmpty')}
                      </p>
                    ) : (
                      <ul className="mt-3 divide-y divide-border">
                        {branch.kiosks.map((d) => (
                          <li key={d.id} className="flex flex-wrap items-center gap-3 py-2">
                            <Monitor className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                              {d.name || d.hostname || d.model_name || `#${d.id}`}
                            </span>
                            {/* Holding a socket open right now is a different
                                fact from "approved", and both matter here. */}
                            {d.status === 'ACTIVE' && (
                              <span className="text-xs text-muted-foreground">
                                {d.socket_connected
                                  ? t('kiosk.settings.online')
                                  : t('kiosk.settings.offline')}
                              </span>
                            )}
                            <StatusPill status={d.status} t={t} />
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* The clock and the ads belong to a branch, so there is
                        nothing to show for a terminal that has not been given one. */}
                    {branch.location && (
                      <>
                        <IdlePanel restaurantId={restaurantId} location={branch.location}
                          canEdit={canEdit} t={t} />
                        <AdsPanel restaurantId={restaurantId} location={branch.location}
                          canEdit={canEdit} t={t} />
                      </>
                    )}
                  </CardContent>
                </Card>
              ))}
            </section>
          ))}
        </>
      )}
    </div>
  );
}
